import PropTypes from 'prop-types';
import React from 'react';
import MessagePane from '../MessagePane/MessagePane';
import Conversation from '../Conversation/Conversation';
import Header from '../Header/Header';
import {wix, serverApi} from 'chat-widget-common';
import {connect} from 'react-redux';
import {RT_EVENT_TYPE} from '../../constants/rt-event-type';
import {CHAT_MESSAGE_TYPE, CHAT_MESSAGE_INNER_TYPE} from '../../constants/chat-message-type';
import {
  addRealtimeMessage,
  showChat,
  autoResponseFlow,
  sendMessage,
  uploadFile,
  updateMessageValue,
  updateMessageValueAndNotify,
  technicalError,
  connectionInfoChanged,
  logWixPubsubEvent
} from '../../actions';
import '../../styles/chat.scss';
import {withExperiments} from 'wix-experiments-react';
import get from 'lodash/get';
import property from 'lodash/property';
import {TECHNICAL_ISSUE} from '../../constants/messages';
import globals from '../../services/globals';
import chatService from '../../services/firebaseChatService';
import fedopsLogger from '../../services/fedopsLogger';
import wixPubSub from '../../services/wixPubSub';
const ONE_DAY = '1440';

class Chat extends React.Component {

  getVisbilitySettings() {
    const installFromAppMarketEnabled = this.props.experiments.enabled('specs.com.InstallChatFromAppMarket');
    const newInstallFlow = this.props.experiments.enabled('specs.crm.NewEditorChatSettingsDefaultsSpec');
    if (installFromAppMarketEnabled && this.props.dummyMode) {
      return serverApi.getEditorSettings(wix.getInstance())
        .then(({editorVisibility, isOnboarded, isInstalled}) => {
          return newInstallFlow ?
            {shouldShowChat: editorVisibility && isOnboarded, isInstalled: !editorVisibility || isOnboarded} : //hack to get rid of isInstalled flag:
            {shouldShowChat: editorVisibility && isOnboarded, isInstalled};
        });
    } else {
      return serverApi.getSettings()
        .then(({chatVisible, chatOnboarded}) => {
          return {shouldShowChat: chatVisible && chatOnboarded};
        });
    }
  }

  initChat() {
    return serverApi.init({deviceType: this.props.isMobile ? 'mobile' : 'desktop'})
      .then(this.setBiShardId.bind(this))
      .then(this.setBiVisitorId.bind(this))
      .then(this.initChatService.bind(this))
      .then(this.props.onChatInit)
      .catch(() => {
        this.props.dispatch(technicalError(true));
      });
  }

  initWixPubSub() {
    if (this.props.experiments.enabled('specs.com.WixWsLoad')) {
      this.pubsub = wixPubSub({
        log: (...params) => this.props.dispatch(logWixPubsubEvent(...params))
      });
      this.pubsub.start();
    }
  }

  componentDidMount() {
    serverApi.setInstance(this.props.instanceId);
    serverApi.setSessionToken(this.props.sessionToken);

    const setVisibilityIfNotInstalled = settings => {
      const {isInstalled} = settings;
      return (isInstalled === false) ?
        serverApi.setEditorVisibilityOn(wix.getInstance()).then(({editorVisibility}) => ({shouldShowChat: editorVisibility})) :
        settings;
    };

    this.getVisbilitySettings()
      .then(setVisibilityIfNotInstalled)
      .then(({shouldShowChat}) => {
        if (shouldShowChat) {
          this.props.onChatVisible();
          this.props.dispatch(showChat({isMobile: this.props.isMobile}));
        }
      })
      .then(() => fedopsLogger.appLoaded());

    if (!this.props.dummyMode) {
      this.initChat()
        .then(() => this.initWixPubSub());
    }
  }

  componentWillUnmount() {
    this.pubsub && this.pubsub.stop();
  }

  initChatService({options, uid, messagePath, presencePath, derivedChatroomId, timestamp, authKey, location, visitorInstanceId}) {
    const timeTillDisconnect = this.props.experiments.enabled('specs.com.DisconnectingIdleVisitor') ? ONE_DAY : null;
    const chatServicePromise = chatService.init({authKey, options, uid, messagePath, presencePath, visitorInstanceId, timeTillDisconnect});
    return chatServicePromise
      .then(chatService => chatService.subscribe({
        messageCallback: this.onIncomingMessage.bind(this),
        timestamp,
        onConnectionInfoChange: connectionInfo => this.props.dispatch(connectionInfoChanged(connectionInfo))
      }))
      .then(chatService => {
        chatService.setLastSeenTimestamp();
        chatService.updatePresence({
          derivedChatroomId,
          timestamp,
          location,
          visitCount: this.props.visitCount,
          deviceType: this.props.isMobile ? 'mobile' : 'desktop'
        });
      })
      .then(() => chatServicePromise);
  }

  onIncomingMessage(message) {
    switch (message.type) {
      case RT_EVENT_TYPE.ACTIVITY:
        if (message.activity.activityType === CHAT_MESSAGE_TYPE && property('activity.info.activityInfo.type')(message) === CHAT_MESSAGE_INNER_TYPE) {
          this.props.dispatch(addRealtimeMessage(message));
          this.props.onAddMessage();
          if (!this.props.chatVisible) {
            this.props.dispatch(showChat({isMobile: this.props.isMobile}));
          }
          if (this.props.collapsed) {
            this.props.onExpand('user');
          }
        }
        break;
      case RT_EVENT_TYPE.VISITOR_MERGE:
        message.visitorId !== message.mergedInto && serverApi.init({
          refresh: true,
          oldVisitorInstanceId: chatService.visitorInstanceId
        })
          .then(this.setBiVisitorId.bind(this))
          .then(this.setBiShardId.bind(this))
          .then(this.initChatService.bind(this));
        break;
      default:
        break;
    }
  }

  setBiVisitorId({visitorId}) {
    globals.visitorId = visitorId;
    return arguments[0];
  }

  setBiShardId({shardId}) {
    globals.shardId = shardId;
    return arguments[0];
  }

  render() {
    const {onExpand, onCollapse, dispatch, messages, collapsed, message, error, chatVisible, chatSettings, dummyMode, isMobile} = this.props;
    const collapsedAsRounded = get(chatSettings, 'layout.collapsedAsRounded');

    const sendFakeMessageWithAutoResponse = message => {
      dispatch(autoResponseFlow(message));
    };

    const updateMessageInput = newValue => dummyMode ? dispatch(updateMessageValue(newValue)) : dispatch(updateMessageValueAndNotify(newValue));

    const handleSend = () => {
      if (dummyMode) {
        sendFakeMessageWithAutoResponse(message.trim());
      } else {
        chatService.goOnlineIfOffline();
        dispatch(sendMessage(message.trim()));
      }
      updateMessageInput('');
    };

    const handleChange = newValue => {
      if (!dummyMode) {
        chatService.goOnlineIfOffline();
      }
      updateMessageInput(newValue);
    };

    const handleFileUpload = file => {
      //if (!dummyMode) {// check
      dispatch(uploadFile(file));
      //}
    };

    const borderRadius = isMobile ? '0' : get(chatSettings, 'design.selectedRadiusOption') + 'px';

    const displayStyle = {
      display: chatVisible ? 'block' : 'none'
    };

    const borderRadiusStyle = {
      borderTopLeftRadius: borderRadius,
      borderTopRightRadius: borderRadius,
    };

    const headerWrapperStyle = (collapsedAsRounded && collapsed) ? displayStyle : Object.assign({}, displayStyle, borderRadiusStyle);
    const collapsedState = collapsed && !isMobile;
    const chatFooter = !error ?
      (<MessagePane
        handleChange={handleChange}
        handleSend={handleSend}
        message={message}
        handleFileUpload={handleFileUpload}
        isMobile={this.props.isMobile}
        />) :
      (<div className="error">
        <p>{TECHNICAL_ISSUE}</p>
      </div>);

    return (

      <div
        aria-expanded={!collapsedState}
        style={headerWrapperStyle}
        className={`chat ${collapsedState ? 'collapsed' : 'expanded'} ${collapsedAsRounded ? 'rounded' : 'bar'} ${isMobile ? 'mobile' : ''}`}
        >
        <Header collapsed={collapsedState} onExpand={() => onExpand('uou')} onCollapse={onCollapse}/>
        {
          !collapsedState ?
            <span className="conversation-container">
              <Conversation error={error} messages={messages}/>
              {chatFooter}
            </span> : null
        }
      </div>
    );
  }
}

Chat.propTypes = {
  dispatch: PropTypes.func,
  onChatInit: PropTypes.func,
  messages: PropTypes.array,
  collapsed: PropTypes.bool,
  message: PropTypes.string,
  lookAndFeel: PropTypes.object,
  error: PropTypes.bool,
  chatVisible: PropTypes.bool,
  experiments: PropTypes.object,
  chatSettings: PropTypes.object,
  dummyMode: PropTypes.bool,
  instanceId: PropTypes.string,
  sessionToken: PropTypes.string,
  biToken: PropTypes.string,
  visitCount: PropTypes.number,
  onCollapse: PropTypes.func,
  onExpand: PropTypes.func,
  isMobile: PropTypes.bool,
  onChatVisible: PropTypes.func,
  onAddMessage: PropTypes.func
};

Chat.defaultProps = {
  onCollapse: () => {},
  onExpand: () => {},
  onChatVisible: () => {},
  onAddMessage: () => {}
};

function mapStateToProps(state) {
  return {
    messages: state.conversationHistory,
    collapsed: state.collapsed,
    message: state.message,
    lookAndFeel: state.lookAndFeel,
    error: state.error,
    chatVisible: state.chatVisible,
    chatSettings: state.chatSettings
  };
}

export default withExperiments(connect(mapStateToProps)(Chat));



// WEBPACK FOOTER //
// ./chat-app/components/Chat/Chat.js